import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../config';

type AdminContextType = {
  isAdmin: boolean;
  adminPin: string | null;
  showPinModal: boolean;
  setShowPinModal: (show: boolean) => void;
  checkPin: (pin: string) => Promise<boolean>;
  logout: () => void;
};

const AdminContext = createContext<AdminContextType | null>(null);

const ADMIN_SESSION_KEY = 'materia_admin_session';
const ADMIN_SESSION_PIN_KEY = 'materia_admin_pin';

export function AdminProvider({ children }: { children: React.ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPin, setAdminPin] = useState<string | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(ADMIN_SESSION_KEY);
    if (stored === 'true') {
      setIsAdmin(true);
      const pin = sessionStorage.getItem(ADMIN_SESSION_PIN_KEY);
      if (pin) setAdminPin(pin);
    }
  }, []);

  const checkPin = useCallback(async (pin: string): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (data.success) {
        setIsAdmin(true);
        setAdminPin(pin);
        sessionStorage.setItem(ADMIN_SESSION_KEY, 'true');
        sessionStorage.setItem(ADMIN_SESSION_PIN_KEY, pin);
        setShowPinModal(false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    setIsAdmin(false);
    setAdminPin(null);
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    sessionStorage.removeItem(ADMIN_SESSION_PIN_KEY);
  }, []);

  return (
    <AdminContext.Provider
      value={{
        isAdmin,
        adminPin,
        showPinModal,
        setShowPinModal,
        checkPin,
        logout,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
